import { useState } from 'react'
import { Link, useParams, useSearchParams } from 'react-router-dom'
import { api } from '../../lib/api.js'
import {
  Badge, Card, Empty, ErrorBox, Field, KV, Modal, Page, Spinner, StatusBadge,
  SubmitButton, useAsync, useConfirm, useToast
} from '../../components/Ui.jsx'
import { BATCH_STATUS, FLAG, fmtDate, fmtStamp, inr, lakh } from '../../lib/format.js'

export default function HatcheryDetail () {
  const { id } = useParams()
  const [params] = useSearchParams()
  const toast = useToast()
  const confirm = useConfirm()
  const { data, error, loading, reload } = useAsync(() => api.get(`/admin/hatcheries/${id}`), [id], { live: ['hatcheries', 'batches', 'orders'] })

  const [tab, setTab] = useState('batches')
  const [issuing, setIssuing] = useState(false)
  const [newPassword, setNewPassword] = useState('')
  const [busy, setBusy] = useState(false)
  const [issued, setIssued] = useState(null)

  if (loading) return <Page title="Hatchery"><Spinner /></Page>
  if (error) return <Page title="Hatchery"><ErrorBox error={error} onRetry={reload} /></Page>

  const { hatchery: h, batches, orders, samples, login, totals } = data
  const live = batches.filter(b => ['active', 'sample_stage'].includes(b.status))
  const availableLakh = live.reduce((s, b) => s + b.availableLakh, 0)
  const flagged = batches.filter(b => b.qualityFlag !== 'clear')
  const media = batches.flatMap(b => (b.media || []).map(m => ({ ...m, batchCode: b.batchCode })))

  const issueCredentials = async e => {
    e.preventDefault()
    const reissue = Boolean(login)
    const ok = await confirm({
      title: reissue ? 'Issue a new password?' : 'Create the portal login?',
      body: reissue
        ? `${login.username} will stop working with the old password immediately. Make sure you can reach ${h.name} to read out the new one.`
        : `${h.name} gets a login it can use straight away to post batches.`,
      facts: [['Hatchery', h.name], ['Username', login?.username || 'derived from the name']],
      confirmLabel: reissue ? 'Issue new password' : 'Create login',
      tone: reissue ? 'danger' : ''
    })
    if (!ok) return
    setBusy(true)
    try {
      const res = await api.post(`/admin/hatcheries/${id}/credentials`, { password: newPassword || undefined })
      setIssued(res.credentials)
      setIssuing(false)
      setNewPassword('')
      toast.ok(res.message)
      reload()
    } catch (err) {
      toast.error(err.message)
    } finally {
      setBusy(false)
    }
  }

  const toggleStatus = async () => {
    const next = h.status === 'active' ? 'suspended' : 'active'
    const suspending = next === 'suspended'
    const ok = await confirm({
      title: suspending ? `Suspend ${h.name}?` : `Reactivate ${h.name}?`,
      body: suspending
        ? 'Its batches drop out of matching and off the public directory straight away, and its login stops working. Bookings already placed are not touched.'
        : 'Its batches can be matched again, it reappears on the public directory, and its login works again.',
      facts: [
        ['Live batches', `${live.length}`],
        ['Open bookings', `${orders.filter(o => !['completed', 'cancelled'].includes(o.status)).length}`]
      ],
      confirmLabel: suspending ? 'Suspend hatchery' : 'Reactivate',
      tone: suspending ? 'danger' : ''
    })
    if (!ok) return
    try {
      const res = await api.post(`/admin/hatcheries/${id}/status`, { status: next })
      toast.ok(res.message)
      reload()
    } catch (err) {
      toast.error(err.message)
    }
  }

  const shown = issued || (params.get('issued') === '1' && login?.issuedSecret
    ? { username: login.username, password: login.issuedSecret }
    : null)

  return (
    <Page
      crumb={<Link to="/admin/hatcheries">← Hatcheries</Link>}
      title={h.name}
      subtitle={`${h.code} · ${h.place}`}
      actions={<>
        <Link className="btn" to={`/hatchery?hatcheryId=${h._id}`}>Open its portal view</Link>
        <Link className="btn" to={`/admin/hatcheries/${h._id}/edit`}>Edit profile</Link>
        <button className={`btn ${h.status === 'active' ? 'danger' : 'primary'}`} onClick={toggleStatus}>
          {h.status === 'active' ? 'Suspend' : 'Reactivate'}
        </button>
      </>}
    >
      {h.status !== 'active' && (
        <div className="alert warn">
          <div><b>This hatchery is suspended.</b>Its batches are hidden from operator matching and its login is disabled.</div>
        </div>
      )}
      {flagged.length > 0 && (
        <div className="alert error">
          <div>
            <b>{flagged.length} batch{flagged.length > 1 ? 'es' : ''} flagged on seed quality</b>
            {flagged.map(b => (
              <div key={b._id}>
                <span className="mono">{b.batchCode}</span> — {FLAG[b.qualityFlag].label}, {b.sampleFailCount} failed sample test{b.sampleFailCount > 1 ? 's' : ''}.{' '}
                <Link to={`/operator/batches/${b._id}/flag`}>Open the decision screen</Link>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="grid four" style={{ marginBottom: 18 }}>
        <div className="stat"><div className="n">{live.length}</div><div className="k">Live batches</div><div className="sub">{batches.length} total</div></div>
        <div className="stat"><div className="n">{lakh(availableLakh)}</div><div className="k">Seed available now</div><div className="sub">unbooked PL across live batches</div></div>
        <div className="stat"><div className="n">{lakh(totals.seedLakh)}</div><div className="k">Seed sold via platform</div><div className="sub">{orders.length} bookings on file</div></div>
        <div className="stat"><div className="n">{inr(totals.paidOut)}</div><div className="k">Paid out to hatchery</div><div className="sub">{inr(totals.commission)} platform commission</div></div>
      </div>

      {shown && (
        <Card title="Login issued — hand these over offline">
          <div className="cred-box">
            <div className="row"><span>Portal URL</span><code>{window.location.origin}/login</code></div>
            <div className="row"><span>Username</span><code>{shown.username}</code></div>
            <div className="row"><span>Password</span><code>{shown.password}</code></div>
          </div>
          <p className="hint">
            Read these out to the hatchery over the phone. The password stays visible here until the
            hatchery signs in and sets its own — after that only a reset can issue a new one.
          </p>
        </Card>
      )}

      <div className="split">
        <div>
          <Card padded={false}>
            <div className="tabs">
              <button className={tab === 'batches' ? 'on' : ''} onClick={() => setTab('batches')}>Batches ({batches.length})</button>
              <button className={tab === 'orders' ? 'on' : ''} onClick={() => setTab('orders')}>Bookings ({orders.length})</button>
              <button className={tab === 'samples' ? 'on' : ''} onClick={() => setTab('samples')}>Sample tests ({samples.length})</button>
              <button className={tab === 'media' ? 'on' : ''} onClick={() => setTab('media')}>Media ({media.length})</button>
            </div>

            {tab === 'batches' && (
              <div className="scroll-x">
                <table>
                  <thead>
                    <tr>
                      <th>Batch</th><th>Ready</th><th>Stage</th><th className="num">Capacity</th>
                      <th className="num">Price/1k</th><th>Quality</th><th>Status</th><th />
                    </tr>
                  </thead>
                  <tbody>
                    {batches.map(b => (
                      <tr key={b._id}>
                        <td>
                          <Link className="rowlink mono" to={`/hatchery/batches/${b._id}`}>{b.batchCode}</Link>
                          <span className="t-sub">{b.species} · {b.broodstockSource}</span>
                          {b.offer?.type && b.offer.type !== 'none' && (
                            <><br /><span className="pill-offer clip" title={b.offer.details || b.offer.type}>{b.offer.details || b.offer.type}</span></>
                          )}
                        </td>
                        <td className="tight">{fmtDate(b.readyDate)}</td>
                        <td>{b.plStage}<span className="t-sub">{b.salinityPpt} ppt</span></td>
                        <td className="num">
                          {lakh(b.availableLakh)}
                          <span className="t-sub">of {lakh(b.totalCapacityLakh)}</span>
                        </td>
                        <td className="num">{inr(b.basePricePer1000)}</td>
                        <td>
                          <Badge tone={FLAG[b.qualityFlag].tone}>{FLAG[b.qualityFlag].label}</Badge>
                          {b.sampleFailCount > 0 && <span className="t-sub">{b.samplePassCount} pass / {b.sampleFailCount} fail</span>}
                        </td>
                        <td><Badge tone={BATCH_STATUS[b.status].tone}>{BATCH_STATUS[b.status].label}</Badge></td>
                        <td className="right">
                          {b.pcr?.reportPath && <a className="btn sm" href={b.pcr.reportPath} target="_blank" rel="noreferrer">PCR</a>}
                        </td>
                      </tr>
                    ))}
                    {!batches.length && <tr><td colSpan={8}><Empty>This hatchery has not listed any batch yet.</Empty></td></tr>}
                  </tbody>
                </table>
              </div>
            )}

            {tab === 'orders' && (
              <div className="scroll-x">
                <table>
                  <thead>
                    <tr><th>Order</th><th>Farmer</th><th>Stocking</th><th className="num">Qty</th><th className="num">Value</th><th>Status</th></tr>
                  </thead>
                  <tbody>
                    {orders.map(o => (
                      <tr key={o._id}>
                        <td>
                          <Link className="rowlink mono" to={`/operator/orders/${o._id}`}>{o.orderCode}</Link>
                          <span className="t-sub mono">{o.batch?.batchCode}</span>
                        </td>
                        <td>{o.farmer?.name}<span className="t-sub">{[o.farmer?.village, o.farmer?.mandal].filter(Boolean).join(', ')}</span></td>
                        <td className="tight">{fmtDate(o.targetStockingDate)}</td>
                        <td className="num">{lakh(o.quantityLakh)}</td>
                        <td className="num">{inr(o.totalAmount)}</td>
                        <td><StatusBadge status={o.status} /></td>
                      </tr>
                    ))}
                    {!orders.length && <tr><td colSpan={6}><Empty>No bookings against this hatchery yet.</Empty></td></tr>}
                  </tbody>
                </table>
              </div>
            )}

            {tab === 'samples' && (
              <div className="scroll-x">
                <table>
                  <thead>
                    <tr><th>Order</th><th>Batch</th><th>Farmer</th><th className="num">Survival</th><th>Result</th><th>Reported</th></tr>
                  </thead>
                  <tbody>
                    {samples.map(s => (
                      <tr key={s._id}>
                        <td><Link className="rowlink mono" to={`/operator/orders/${s._id}`}>{s.orderCode}</Link></td>
                        <td className="mono small">{s.batch?.batchCode}</td>
                        <td>{s.farmer?.name}<span className="t-sub">{s.farmer?.village}</span></td>
                        <td className="num">{s.sample.survivalPct}%<span className="t-sub">{s.sample.survivors}/{s.sample.animalsSent}</span></td>
                        <td><Badge tone={s.sample.result === 'pass' ? 'good' : 'bad'}>{s.sample.result === 'pass' ? 'Passed' : 'Failed'}</Badge></td>
                        <td className="tight small">{fmtStamp(s.sample.reportedAt)}</td>
                      </tr>
                    ))}
                    {!samples.length && <tr><td colSpan={6}><Empty>No sample tests reported for this hatchery yet.</Empty></td></tr>}
                  </tbody>
                </table>
              </div>
            )}

            {tab === 'media' && (
              <div className="body">
                {media.length
                  ? (
                    <div className="media-strip">
                      {media.map(m => (
                        <figure key={m._id}>
                          {m.kind === 'video'
                            ? <video src={m.path} controls />
                            : <a href={m.path} target="_blank" rel="noreferrer"><img src={m.path} alt={m.caption || m.batchCode} /></a>}
                          <figcaption>{m.caption || m.batchCode}<br /><span className="mono">{m.batchCode}</span></figcaption>
                        </figure>
                      ))}
                    </div>
                    )
                  : <Empty>No tank photos or clips uploaded yet.</Empty>}
              </div>
            )}
          </Card>
        </div>

        <div>
          <Card title="Profile">
            <KV items={[
              { k: 'Owner / contact', v: h.ownerName },
              { k: 'Phone', v: h.phone },
              { k: 'Email', v: h.email },
              { k: 'CAA registration', v: <span className="mono">{h.caaRegistrationNo || '—'}</span> },
              { k: 'Annual capacity', v: h.annualCapacityCr ? `${h.annualCapacityCr} crore PL` : '—' },
              { k: 'Coordinates', v: <span className="mono small">{h.lat}, {h.lng}</span> }
            ]} />
            {h.broodstockPartners && <>
              <div className="divider" />
              <div className="k" style={{ fontSize: 11, textTransform: 'uppercase', color: 'var(--muted)' }}>Broodstock partners</div>
              <div>{h.broodstockPartners}</div>
            </>}
            {h.biosecurityNotes && <>
              <div className="divider" />
              <div className="k" style={{ fontSize: 11, textTransform: 'uppercase', color: 'var(--muted)' }}>Biosecurity</div>
              <div className="small">{h.biosecurityNotes}</div>
            </>}
            {h.address && <>
              <div className="divider" />
              <div className="small muted">{h.address}</div>
            </>}
          </Card>

          <Card title="Portal login" actions={
            <button className="btn sm" onClick={() => setIssuing(true)}>
              {login ? 'Reset password' : 'Create login'}
            </button>
          }>
            {login
              ? (
                <KV items={[
                  { k: 'Username', v: <span className="mono">{login.username}</span> },
                  { k: 'Status', v: <Badge tone={login.active ? 'good' : 'muted'}>{login.active ? 'Enabled' : 'Disabled'}</Badge> },
                  {
                    k: 'Password on file',
                    v: login.issuedSecret
                      ? <span className="mono">{login.issuedSecret}</span>
                      : <span className="muted small">Changed by the hatchery — reset to issue a new one</span>
                  },
                  { k: 'Issued', v: fmtStamp(login.issuedAt) },
                  { k: 'Last sign-in', v: fmtStamp(login.lastLoginAt) }
                ]} />
                )
              : <Empty>No login issued yet for this hatchery.</Empty>}
          </Card>
        </div>
      </div>

      {issuing && (
        <Modal title={login ? 'Issue a new password' : 'Create portal login'} onClose={() => setIssuing(false)}>
          <form onSubmit={issueCredentials}>
            <p className="muted small" style={{ marginTop: 0 }}>
              {login
                ? <>The current password for <span className="mono">{login.username}</span> stops working immediately. Read the new one out to the hatchery.</>
                : <>A username is derived from the hatchery name unless you set one.</>}
            </p>
            <Field label="Password" hint="Leave blank to generate a readable one automatically.">
              <input value={newPassword} onChange={e => setNewPassword(e.target.value)} autoComplete="off" placeholder="auto-generate" />
            </Field>
            <div className="btnrow">
              <SubmitButton busy={busy} type="submit">{login ? 'Reset password' : 'Create login'}</SubmitButton>
              <button type="button" className="btn" onClick={() => setIssuing(false)}>Cancel</button>
            </div>
          </form>
        </Modal>
      )}
    </Page>
  )
}
